import { getUrlParts } from '@/functions/getUrlParts';
import { getEnvironmentConstants, isEnvironment } from '@/functions/getEnvironmentConstants';
import { type Environment, environmentNames } from '@/constants';

const getEnvironmentFromWebAppUrl = (): Environment => {
  const urlParts = getUrlParts();
  switch (urlParts.subdomain.toLowerCase()) {
    case 'local':
    case 'local.app':
      return environmentNames.LOCAL;
    case 'dev':
    case 'dev.app':
    case 'dev-view':
      return environmentNames.DEV;
    case 'qa':
    case 'qa.app':
      return environmentNames.QA;
    default:
      return environmentNames.PROD;
  }
};

/**
 * @name EnvironmentHelper
 * @description The `EnvironmentHelper` class provides a helpful utility
 * for handling environment related information.
 */
class EnvironmentHelper {
  protected env: Environment = 'prod';

  /**
   * Sets the environment
   */
  setEnvironment(environment: Environment) {
    if (!isEnvironment(environment)) {
      throw new Error(`Invalid environment: ${environment}`);
    }
    this.env = environment;
  }

  setEnvironmentFromWebAppUrl() {
    const envFromWebAppUrl = getEnvironmentFromWebAppUrl();
    this.setEnvironment(envFromWebAppUrl);
  }

  /**
   * Retrieves the environment from the class
   */
  getEnvironment() {
    return this.env;
  }

  /**
   * Retrieve the environment as an uppercase string
   */
  getUppercaseEnvironment() {
    return this.env.toUpperCase() as Uppercase<Environment>;
  }

  /**
   * @name getUrlPrefix
   * @deprecated Instead of constructing hostnames dynamically, use functions
   * getAppUrl, getApiUrl, and getLegacyApiUrl
   */
  getUrlPrefix() {
    let environmentPrefix: string = this.env;
    if (environmentPrefix === 'local' || environmentPrefix === 'dev') {
      environmentPrefix += '.';
    } else {
      environmentPrefix = '';
    }

    return environmentPrefix;
  }

  /**
   * @name getAppUrl
   * @returns Returns the url of the app for the current environment
   */
  getAppUrl() {
    const { webapp } = this.getEnvironmentConfig();
    const { appUrl } = webapp;
    return appUrl;
  }

  /**
   * @name getApiUrl
   * @returns {string} Returns the url of the api for the current environment
   */
  getApiUrl() {
    const { webapp } = this.getEnvironmentConfig();
    const { apiV2Url } = webapp;
    return apiV2Url;
  }

  getMetroUrl() {
    const { metro } = this.getEnvironmentConfig();
    const { baseUrl } = metro;
    return baseUrl;
  }

  getYardstickUrl() {
    const { yardstick } = this.getEnvironmentConfig();
    const { baseUrl } = yardstick;
    return baseUrl;
  }

  /**
   * @name getLegacyApiUrl
   * @returns {string} Returns the url of the V1 api for the current environment
   */
  getLegacyApiUrl() {
    const { webapp } = this.getEnvironmentConfig();
    const { apiLegacyUrl } = webapp;
    return apiLegacyUrl;
  }

  /**
   * @name getEnvironmentConfig
   * @description gets the environment config for the current environment
   */
  getEnvironmentConfig() {
    return getEnvironmentConstants(this.env);
  }

  /**
   * @name isProduction
   * @description Determines if the provided environment is the production environment.
   * @deprecated Instead push configuration into @bblabs/constants library Environments object
   * @returns true if the environment is equal to the string 'PROD'; false otherwise. */
  isProduction() {
    return this.env.toUpperCase() === 'PROD';
  }

  /**
   * @name isLocal
   * @description Determines if the provided environment is the local environment.
   * @deprecated Instead push configuration into @bblabs/constants library Environments object
   * @returns true if the environment is equal to the string 'LOCAL'; false otherwise. */
  isLocal() {
    return this.env.toUpperCase() === 'LOCAL';
  }

  getReactionServiceUrl() {
    const { reaction } = this.getEnvironmentConfig();
    const { baseUrl } = reaction;
    return baseUrl;
  }
}

export default new EnvironmentHelper();
